import DOMPurify from 'dompurify';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { PageContent } from '../types';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import { useToast } from '../hooks/useToast';
import { AccountPanel } from '../components/dashboard/AccountPanel';
import { SiteManagementPanel } from '../components/dashboard/SiteManagementPanel';
import { UsersPanel } from '../components/dashboard/UsersPanel';
import { RedirectsPanel } from '../components/dashboard/RedirectsPanel';
import { FavoritesPanel } from '../components/dashboard/FavoritesPanel';
import { HistoryPanel } from '../components/dashboard/HistoryPanel';
import { BlogPanel } from '../components/dashboard/BlogPanel';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const [pageContent, setPageContent] = useState<PageContent>(allContent.dashboard);
  const { showToast } = useToast();
  
  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, 'dashboard', onSeoApplied);
  
  const isAdmin = user && user.role === 'admin';
  const [activeTab, setActiveTab] = useState('account');

  useEffect(() => {
    setPageContent(allContent.dashboard);
  }, [allContent.dashboard]);
  
  const handleSave = async () => {
    const oldSlug = allContent.dashboard?.seo?.slug;
    const redirectCreated = await updatePageContent('dashboard', pageContent);
    showToast('Dashboard page content saved!');
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };

  if (!user) {
    return null; 
  }

  const tabClass = (tabName: string) => `whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer ${activeTab === tabName ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`;

  const welcomeMessageHtml = pageContent.welcomeMessage
    ? pageContent.welcomeMessage
        .replace('{userName}', user.name)
        .replace('{userEmail}', user.email)
    : '';
  
  const sanitizedWelcomeMessage = DOMPurify.sanitize(welcomeMessageHtml, { ADD_ATTR: ['class'] });

  return (
    <div className="w-full max-w-6xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-[#4F46E5] mb-2">
            {pageContent.h1}
          </h1>
          <div 
            className="text-[#6B7280] mb-6 prose max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizedWelcomeMessage }}
          />
        </div>

        {isAdminMode && (
          <AdminControls 
            pageId="dashboard" 
            pageContent={pageContent} 
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}
        
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button onClick={() => setActiveTab('account')} className={tabClass('account')}>Account &amp; Stats</button>
            <button onClick={() => setActiveTab('favorites')} className={tabClass('favorites')}>Saved Names</button>
            <button onClick={() => setActiveTab('history')} className={tabClass('history')}>History</button>
            {isAdmin && (
              <>
                <button onClick={() => setActiveTab('siteManagement')} className={tabClass('siteManagement')}>Site Management</button>
                <button onClick={() => setActiveTab('users')} className={tabClass('users')}>User List</button>
                <button onClick={() => setActiveTab('redirects')} className={tabClass('redirects')}>Redirects</button>
                <button onClick={() => setActiveTab('blog')} className={tabClass('blog')}>Blog Management</button>
              </>
            )}
          </nav>
        </div>

        {activeTab === 'account' && <AccountPanel />}
        {activeTab === 'favorites' && <FavoritesPanel />}
        {activeTab === 'history' && <HistoryPanel />}
        {isAdmin && activeTab === 'siteManagement' && <SiteManagementPanel />}
        {isAdmin && activeTab === 'users' && <UsersPanel />}
        {isAdmin && activeTab === 'redirects' && <RedirectsPanel />}
        {isAdmin && activeTab === 'blog' && <BlogPanel />}
      </div>
    </div>
  );
};
